import {
  $connectedServer,
  $connectionRole,
  $effectiveRemoteServer,
  exportLogbookContext,
  replaceLogbookContext,
  setConnectedServer,
  setConnectionReadonlyMode,
} from '@/stores';
import type { ExportedLogbookContext } from '@/stores';
import { IS_TAURI } from '@/utils/platformUtils';
import { useStore } from '@nanostores/react';
import { invoke } from '@tauri-apps/api/core';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

type RemoteSnapshotResponse = {
  ok: boolean;
  version: number;
  data: ExportedLogbookContext | null;
};

function serverBaseUrl(ip: string, port: number): string {
  const proto = port === 443 ? 'https' : 'http';
  const host = ip.includes(':') ? `[${ip}]` : ip;
  return `${proto}://${host}:${port}`;
}

export function useServerSync() {
  const role = useStore($connectionRole);
  const effectiveRemoteServer = useStore($effectiveRemoteServer);
  const connectedServer = useStore($connectedServer);
  const lastVersionRef = useRef<number>(-1);
  const suppressDisconnectToastRef = useRef(false);

  useEffect(() => {
    if (role === 'hosting' && connectedServer) {
      setConnectedServer(null);
    }
  }, [role, connectedServer]);

  useEffect(() => {
    if (!connectedServer && suppressDisconnectToastRef.current) {
      suppressDisconnectToastRef.current = false;
      return;
    }

    if (!connectedServer || role !== 'disconnected') {
      return;
    }

    toast.error('Disconnected from server');
  }, [connectedServer, role]);

  useEffect(() => {
    setConnectionReadonlyMode(role === 'connected');
  }, [role]);

  useEffect(() => {
    if (role !== 'connected' || !effectiveRemoteServer) {
      lastVersionRef.current = -1;
      return;
    }

    const base = serverBaseUrl(
      effectiveRemoteServer.ip,
      effectiveRemoteServer.port,
    );
    let cancelled = false;

    const syncOnce = async () => {
      try {
        const response = await fetch(`${base}/api/logbook`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = (await response.json()) as RemoteSnapshotResponse;
        if (!payload.ok || !payload.data) {
          return;
        }

        if (payload.version === lastVersionRef.current) {
          return;
        }

        if (cancelled) {
          return;
        }

        replaceLogbookContext(payload.data);
        lastVersionRef.current = payload.version;
      } catch {
        toast.error('Lost connection to server');
        suppressDisconnectToastRef.current = true;
        setConnectedServer(null);
      }
    };

    void syncOnce();
    const intervalId = window.setInterval(() => {
      void syncOnce();
    }, 1500);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [role, effectiveRemoteServer]);

  useEffect(() => {
    if (!IS_TAURI || role !== 'hosting') {
      return;
    }

    let cancelled = false;

    const publishSnapshot = async () => {
      try {
        await invoke('set_server_snapshot', {
          snapshot: exportLogbookContext(),
        });
      } catch {
        // Hosting can continue even if an update fails.
      }
    };

    void publishSnapshot();
    const intervalId = window.setInterval(() => {
      if (cancelled) {
        return;
      }

      void publishSnapshot();
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [role]);
}
